import { createSelector } from './selectorWrapper'
import { pageSelectors, simpleSelectors } from './basicState'
import { TitleIdToAction, ActionFragment } from '../reducers/action'
import { getPlaybackIds } from '../utils/playbackActionHelper'
import { btfActions, playbackFirstChildSelector } from './actionFragment'
import { PageType, PlayerConfigData } from '../integrations/player/config'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { getPlaybackClickstreamRef } from '../utils/getPlaybackClickstreamRef'
import { TRAILER_REFTAG } from '../components/playback/trailer'
import { parametersSelector } from './requestContext'
import { Parameters } from 'DVWebNodeUtils'

export type PlaybackAsinToCanonicalId = { [offerAsin: string]: string }

export enum Autoplay {
  feature = 'Feature',
  trailer = 'Trailer',
}

export const playerConfigDataSelector = createSelector(
  [simpleSelectors.context],
  ({ sessionID: sessionId, marketplaceID: marketplaceId, customerID: customerId, locale }) => (
    {
      sessionId,
      marketplaceId,
      customerId,
      locale,
      playerBeta: false,
      useDefaultView: true,
      pageType: (global.__NATIVEAPP__ ? 'SPA' : 'Detail') as PageType,
    } as PlayerConfigData
  )
)

export const autoplaySelector = createSelector(
  [parametersSelector],
  (parameters: Parameters) => {
    const autoplay = parameters.autoplay && parameters.autoplay.value

    return autoplay === 'trailer' ? Autoplay.trailer
      : autoplay === '1' ? Autoplay.feature
      : undefined
  }
)

export const whisperCacheDataSelector = createSelector(
  [playbackFirstChildSelector, pageSelectors.trailer, autoplaySelector],
  (
    playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps,
    trailerFragment?: WpsRuntime.atv.wps.TrailerFragmentModelProps,
    autoplay?: undefined | Autoplay
  ) => (
      !autoplay && playbackData && getFeatureContentConfig(playbackData)
      || !autoplay && trailerFragment && getTrailerContentConfig(trailerFragment)
        || undefined
  )
)

const getFeatureContentConfig = (playbackData: WpsRuntime.atv.wps.PlaybackActionDataProps) => {
  return {
    asin: playbackData.playbackID,
    trailer: false,
    position: playbackData.resumeTime || 0,
    ref: getPlaybackClickstreamRef(playbackData.contentType, playbackData.entitlementType),
    videoType: playbackData.videoMaterialType,
    useFullView: true,
  }
}

const getTrailerContentConfig = (trailerFragment: WpsRuntime.atv.wps.TrailerFragmentModelProps) => {
  return {
    asin: trailerFragment.playbackID,
    trailer: true,
    position: 0,
    ref: TRAILER_REFTAG,
    videoType: 'Trailer',
    useFullView: true,
  }
}

export const playbackIdToCanonicalIdSelector = createSelector(
  [simpleSelectors.pageTitleId, pageSelectors.actionAtf, btfActions],
  (
    pageTitleId: string,
    atfAction: ActionFragment,
    btfActions: TitleIdToAction
  ) => {
    const result: PlaybackAsinToCanonicalId = {}
    Object.entries({ [pageTitleId]: atfAction, ...btfActions })
      .filter(([, value]) => Boolean(value))
      .forEach(([canonicalId, action]) => {
        const { playbackActions } = action!
        const playbackIds = getPlaybackIds(playbackActions)
        if (playbackIds) {
          playbackIds.forEach(asin => {
            result[asin] = canonicalId
          })
        }
      })
    return result
  }
)
